import {
  ArrowRightLeft,
  Box,
  Clapperboard,
  Film,
  Folder,
  FolderOpen,
  FolderUp,
  Image as ImageIcon,
  MapPin,
  Package,
  Palette,
  PenLine,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useTranslation } from "@/hooks/use-translation"
import {
  isLibraryFlowOrphan,
  type LibraryCanvasRow,
  type LibraryEntitySections,
  type LibraryFlowRow,
  type LibrarySketchesSummary,
  type LibraryUploadsSummary,
  type LibraryTree as LibraryTreeData,
} from "@/lib/library-api"
import { LIBRARY_ENTITY_SECTIONS, type LibraryEntitySection, type LibrarySelection } from "@/lib/library-url"
import { UI_NAV_ITEM_ACTIVE, UI_NAV_ITEM_IDLE } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"

type LibraryTreeProps = {
  projectId: number
  tree: LibraryTreeData
  canvases: LibraryCanvasRow[]
  flows: LibraryFlowRow[]
  /** Герои, локации и реквизит — по секции на вид. */
  entities: LibraryEntitySections
  sketches: LibrarySketchesSummary
  uploads: LibraryUploadsSummary
  activeEpisodeId: number | null
  selection: LibrarySelection
  onSelect: (selection: LibrarySelection) => void
  /** true — клавиатурный фокус сейчас в дереве (стрелки ведут курсор здесь). */
  hasFocus?: boolean
  /** Стрелка вправо / Enter на листе — уходим в блок с ассетами. */
  onEnterContent?: () => void
}

/** Атрибут ряда дерева: по нему страница отличает клик по навигации от клика в пустоту. */
export const LIBRARY_TREE_ITEM_ATTR = "data-library-tree-item"
const TREE_ITEM_ATTR = LIBRARY_TREE_ITEM_ATTR

/**
 * Отступ ряда по уровню вложенности: плитки эпизода, сцены и шота стоят каждая
 * в своей колонке, направляющая уровня идёт по левому краю родительской плитки.
 */
const INDENT = { episode: 4, scene: 26, shot: 48 } as const

type TreeRow = {
  key: string
  selection: LibrarySelection
  /** Раскрывающиеся узлы — только эпизод и сцена; остальное листья. */
  collapsibleKey?: string
  parentKey?: string
}

function collapseKey(kind: "episode" | "scene", id: number): string {
  return `${kind === "episode" ? "e" : "s"}${id}`
}

function selectionKey(selection: LibrarySelection): string {
  if (selection.section === "canvas") return selection.canvasId != null ? `c${selection.canvasId}` : "canvas"
  if (selection.section === "flow") {
    if (selection.orphans) return "flow-orphans"
    return selection.flowId != null ? `f${selection.flowId}` : "flow"
  }
  if (selection.section === "sketches") return "sketches"
  if (selection.section === "uploads") return "uploads"
  if (selection.section !== "shots") {
    return selection.entityId != null ? `${selection.section}:${selection.entityId}` : selection.section
  }
  const node = selection.node
  if (!node) return "shots"
  if (node.level === "episode") return collapseKey("episode", node.episodeId)
  if (node.level === "animatic") return `an${node.episodeId}`
  if (node.level === "scene") return collapseKey("scene", node.sceneId)
  return `sh${node.shotId}`
}

// Дефолт раскрывашек: развёрнут только активный эпизод; в больших эпизодах
// (> 8 сцен — порог из Assembly) сцены дополнительно свёрнуты.
function defaultCollapsed(tree: LibraryTreeData, activeEpisodeId: number | null): Set<string> {
  const out = new Set<string>()
  for (const season of tree.seasons) {
    for (const episode of season.episodes) {
      if (episode.id !== activeEpisodeId) out.add(collapseKey("episode", episode.id))
      if (episode.scenes.length > 8) {
        for (const scene of episode.scenes) out.add(collapseKey("scene", scene.id))
      }
    }
  }
  return out
}

/** Липкий заголовок секции: при скролле видно, в какой части дерева находишься. */
function SectionHeader({ label, icon: Icon, className }: { label: string; icon: LucideIcon; className?: string }) {
  return (
    <div
      className={cn(
        // Фон непрозрачный, иначе ряды просвечивают из-под липкого заголовка:
        // bg-ui-bg-elevated совпадает и с поверхностью карточки раздела, и с панелью модалки.
        // Смещение липкости отрицательное: область залипания — это скроллпорт, ВЫЧТЕННЫЙ на
        // padding колонки (p-2), поэтому при top-0 заголовок замирает на 8px ниже видимого края
        // и в этой щели видно проезжающие ряды. -top-2 сажает его ровно на край.
        "sticky -top-2 z-10 flex items-center gap-1.5 bg-ui-bg-elevated px-2 pb-2",
        "text-[10px] tracking-[0.09em] text-ui-text-faint uppercase",
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </div>
  )
}

/** Иконки секций те же, что у сущностей в остальном воркспейсе. */
const SECTION_ICON: Record<"shots" | "canvas" | "flow" | "sketches" | "uploads" | LibraryEntitySection, LucideIcon> = {
  shots: Clapperboard,
  characters: Users,
  locations: MapPin,
  props: Package,
  scenes_3d: Box,
  canvas: Palette,
  flow: Workflow,
  sketches: PenLine,
  uploads: FolderUp,
}

/** Направляющая уровня: вертикальная линия по детям раскрытого узла. */
function TreeGuide({ left }: { left: number }) {
  return <span aria-hidden className="absolute top-0 bottom-0 w-px bg-ui-border" style={{ left }} />
}

/**
 * Слот 20×20 в начале ряда — один на все уровни: у контейнера папка, у шота кроп
 * кадра. Формат кадра на ширину ряда не влияет, поэтому 9:16 и 16:9 стоят в одной
 * колонке и текст рядом не пляшет.
 */
function TreeTile({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn("flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded border", className)}
    >
      {children}
    </span>
  )
}

/** Папка контейнера: раскрытость держит сама иконка, отдельного шеврона нет. */
function FolderTile({ open, accent = false }: { open: boolean; accent?: boolean }) {
  const Icon = open ? FolderOpen : Folder
  return (
    <TreeTile
      className={accent ? "border-ui-accent/30 bg-ui-accent/10" : "border-ui-border-strong bg-ui-surface-raised"}
    >
      <Icon
        className={cn(
          "h-3.5 w-3.5",
          accent ? "text-ui-accent-text" : open ? "text-ui-text-secondary" : "text-ui-text-muted",
        )}
      />
    </TreeTile>
  )
}

/** Счётчик справа: у контейнера — сколько внутри, у шота — сколько ассетов в папке. */
function NodeCount({ count, tone = "faint" }: { count: number; tone?: "faint" | "muted" }) {
  if (count <= 0) return null
  return (
    <span
      className={cn(
        "ml-auto shrink-0 font-mono text-[10.5px] tabular-nums",
        tone === "muted" ? "text-ui-text-muted" : "text-ui-text-faint",
      )}
    >
      {count}
    </span>
  )
}

/** Ряд дерева: вся строка — одна мишень, у папок клик заодно раскрывает и сворачивает. */
function TreeRowButton({
  itemKey,
  indent,
  active,
  focused,
  bright = false,
  expanded,
  onClick,
  children,
}: {
  itemKey: string
  indent: number
  active: boolean
  focused: boolean
  bright?: boolean
  expanded?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      {...{ [TREE_ITEM_ATTR]: itemKey }}
      tabIndex={focused ? 0 : -1}
      aria-current={active ? "true" : undefined}
      aria-expanded={expanded}
      style={{ paddingLeft: indent }}
      className={cn(
        "flex h-[30px] w-full items-center gap-1.5 rounded-lg pr-2 text-left text-[13px] transition-colors",
        active ? UI_NAV_ITEM_ACTIVE : cn(UI_NAV_ITEM_IDLE, bright && "text-ui-text-primary"),
        focused && "ring-2 ring-ui-accent outline-none ring-inset",
      )}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/**
 * Плоская папка проекта — «Загрузки» и «Эскизы». Заголовка секции у них нет намеренно:
 * он повторял бы подпись ряда слово в слово и той же иконкой, а группировать ему нечего —
 * второй строки здесь быть не может (у этих секций в LibrarySelection нет даже поля id).
 */
function FlatFolderRow({
  itemKey,
  label,
  icon: Icon,
  count,
  active,
  focused,
  onClick,
}: {
  itemKey: string
  label: string
  icon: LucideIcon
  count: number
  active: boolean
  focused: boolean
  onClick: () => void
}) {
  return (
    // Отступ, который раньше давал заголовок секции: ряд отбивается от соседней группы.
    <div className="pt-3.5">
      <TreeRowButton itemKey={itemKey} indent={INDENT.episode} active={active} focused={focused} onClick={onClick}>
        <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
          <Icon className="h-3.5 w-3.5 text-ui-text-muted" />
        </TreeTile>
        <span className="truncate">{label}</span>
        <NodeCount count={count} />
      </TreeRowButton>
    </div>
  )
}

/** Навигация Библиотеки: только папки — сезоны/эпизоды/сцены/шоты, доски Холста, загрузки проекта. */
export function LibraryTree({
  projectId,
  tree,
  canvases,
  flows,
  entities,
  sketches,
  uploads,
  activeEpisodeId,
  selection,
  onSelect,
  hasFocus = false,
  onEnterContent,
}: LibraryTreeProps) {
  const { t } = useTranslation(["workspace", "common"])
  const storageKey = `library-tree-collapsed-${projectId}`
  const containerRef = useRef<HTMLDivElement | null>(null)
  // true — курсор сменила сама выбранная папка (клик уже свершился, клавиатура, крошки).
  // Пока false, нельзя перетягивать фокус на старый ряд: mousedown по другой папке
  // успевает сфокусировать её раньше click, и scrollIntoView уводит её из-под курсора.
  const revealCursorRef = useRef(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) return new Set(JSON.parse(raw) as string[])
    } catch {
      /* ignore */
    }
    return defaultCollapsed(tree, activeEpisodeId)
  })
  const [cursorKey, setCursorKey] = useState<string>(() => selectionKey(selection))

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...collapsed]))
    } catch {
      /* ignore */
    }
  }, [storageKey, collapsed])

  // Индексы предков для авто-раскрытия выбранной папки (deep link, хлебные крошки).
  const parents = useMemo(() => {
    const episodeByScene = new Map<number, number>()
    const byShot = new Map<number, { episodeId: number; sceneId: number }>()
    for (const season of tree.seasons) {
      for (const episode of season.episodes) {
        for (const scene of episode.scenes) {
          episodeByScene.set(scene.id, episode.id)
          for (const shot of scene.shots) byShot.set(shot.id, { episodeId: episode.id, sceneId: scene.id })
        }
      }
    }
    return { episodeByScene, byShot }
  }, [tree])

  // Раскрываем только ПРЕДКОВ выбранного узла: своей раскрытостью узел управляет
  // кликом по ряду, и форсировать её здесь значит отменять этот же клик.
  useEffect(() => {
    if (selection.section !== "shots" || !selection.node) return
    const node = selection.node
    const keys: string[] = []
    if (node.level === "animatic") {
      keys.push(collapseKey("episode", node.episodeId))
    } else if (node.level === "scene") {
      const episodeId = parents.episodeByScene.get(node.sceneId)
      if (episodeId != null) keys.push(collapseKey("episode", episodeId))
    } else if (node.level === "shot") {
      const path = parents.byShot.get(node.shotId)
      if (path) keys.push(collapseKey("episode", path.episodeId), collapseKey("scene", path.sceneId))
    }
    if (keys.length === 0) return
    setCollapsed((prev) => {
      if (!keys.some((key) => prev.has(key))) return prev
      const next = new Set(prev)
      for (const key of keys) next.delete(key)
      return next
    })
  }, [selection, parents])

  // Внешняя смена папки (крошки, переход в просмотрщике) двигает и курсор.
  const selectedKey = selectionKey(selection)
  useEffect(() => {
    revealCursorRef.current = true
    setCursorKey(selectedKey)
  }, [selectedKey])

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const shotsNode = selection.section === "shots" ? selection.node : null
  const hasEpisodes = tree.seasons.some((season) => season.episodes.length > 0)
  const shotsLabel = t("library.sections.shots")

  // Плоский список видимых рядов — порядок клавиатурного обхода.
  const rows = useMemo<TreeRow[]>(() => {
    const out: TreeRow[] = []
    for (const season of tree.seasons) {
      for (const episode of season.episodes) {
        const episodeKey = collapseKey("episode", episode.id)
        out.push({
          key: episodeKey,
          selection: { section: "shots", node: { level: "episode", episodeId: episode.id } },
          collapsibleKey: episodeKey,
        })
        if (collapsed.has(episodeKey)) continue
        if (episode.animatic_segment_count > 0) {
          out.push({
            key: `an${episode.id}`,
            selection: { section: "shots", node: { level: "animatic", episodeId: episode.id } },
            parentKey: episodeKey,
          })
        }
        for (const scene of episode.scenes) {
          const sceneKey = collapseKey("scene", scene.id)
          out.push({
            key: sceneKey,
            selection: { section: "shots", node: { level: "scene", sceneId: scene.id } },
            collapsibleKey: sceneKey,
            parentKey: episodeKey,
          })
          if (collapsed.has(sceneKey)) continue
          for (const shot of scene.shots) {
            out.push({
              key: `sh${shot.id}`,
              selection: { section: "shots", node: { level: "shot", shotId: shot.id } },
              parentKey: sceneKey,
            })
          }
        }
      }
    }
    // Порядок обхода клавиатурой совпадает с порядком секций на экране.
    out.push({ key: "uploads", selection: { section: "uploads" } })
    for (const section of LIBRARY_ENTITY_SECTIONS) {
      for (const row of entities[section]) {
        out.push({ key: `${section}:${row.id}`, selection: { section, entityId: row.id } })
      }
    }
    for (const board of canvases) {
      out.push({ key: `c${board.id}`, selection: { section: "canvas", canvasId: board.id } })
    }
    for (const board of flows) {
      if (isLibraryFlowOrphan(board)) {
        out.push({ key: "flow-orphans", selection: { section: "flow", flowId: null, orphans: true } })
      } else {
        out.push({ key: `f${board.id}`, selection: { section: "flow", flowId: board.id } })
      }
    }
    out.push({ key: "sketches", selection: { section: "sketches" } })
    return out
  }, [tree, canvases, flows, entities, collapsed])

  const moveCursor = useCallback(
    (key: string) => {
      setCursorKey(key)
      const row = rows.find((item) => item.key === key)
      if (row) onSelect(row.selection)
    },
    [rows, onSelect],
  )

  // Фокус ведём вручную: активный ряд получает tabIndex 0 и реальный focus.
  useEffect(() => {
    const revealCursor = revealCursorRef.current
    revealCursorRef.current = false
    if (!hasFocus) return
    const node = containerRef.current?.querySelector<HTMLElement>(`[${TREE_ITEM_ATTR}="${CSS.escape(cursorKey)}"]`)
    if (!node) return
    const activeKey =
      document.activeElement instanceof HTMLElement ? document.activeElement.getAttribute(TREE_ITEM_ATTR) : null
    // mousedown по другому ряду уже поставил на него фокус, click ещё впереди.
    // Вернуть фокус на старый курсор и подскроллить к нему — значит увести ряд
    // из-под указателя, и папка не выберется.
    if (!revealCursor && activeKey != null && activeKey !== cursorKey) return
    node.focus({ preventScroll: true })
    node.scrollIntoView({ block: "nearest" })
  }, [hasFocus, cursorKey])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const index = rows.findIndex((row) => row.key === cursorKey)
    const current = index >= 0 ? rows[index] : null

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        if (index < rows.length - 1) moveCursor(rows[index + 1].key)
        return
      case "ArrowUp":
        e.preventDefault()
        if (index > 0) moveCursor(rows[index - 1].key)
        return
      // Стрелки вбок раскрывают и сворачивают узел (канон дерева), Enter — проваливается в содержимое.
      case "ArrowRight":
        e.preventDefault()
        if (current?.collapsibleKey && collapsed.has(current.collapsibleKey)) toggleCollapsed(current.collapsibleKey)
        return
      case "ArrowLeft": {
        e.preventDefault()
        if (!current) return
        if (current.collapsibleKey && !collapsed.has(current.collapsibleKey)) toggleCollapsed(current.collapsibleKey)
        else if (current.parentKey) moveCursor(current.parentKey)
        return
      }
      case "Enter":
        e.preventDefault()
        onEnterContent?.()
        return
      case "Home":
        e.preventDefault()
        if (rows.length > 0) moveCursor(rows[0].key)
        return
      case "End":
        e.preventDefault()
        if (rows.length > 0) moveCursor(rows[rows.length - 1].key)
        return
    }
  }

  const rowFocused = (key: string) => hasFocus && cursorKey === key

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      {!hasEpisodes ? (
        <>
          <SectionHeader label={shotsLabel} icon={SECTION_ICON.shots} className="pt-1" />
          <p className="px-2 text-xs text-ui-text-muted">{t("library.tree.noShots")}</p>
        </>
      ) : (
        tree.seasons.map((season, seasonIndex) => (
          <div key={season.id}>
            <SectionHeader
              label={`${shotsLabel} · ${t("library.tree.season", { number: season.number })}`}
              icon={SECTION_ICON.shots}
              className={seasonIndex === 0 ? "pt-1" : "pt-3.5"}
            />
            {season.episodes.map((episode) => {
              const episodeKey = collapseKey("episode", episode.id)
              const episodeCollapsed = collapsed.has(episodeKey)
              const episodeActive = shotsNode?.level === "episode" && shotsNode.episodeId === episode.id
              const episodeTotal = episode.scenes.reduce((total, scene) => total + scene.shots.length, 0)
              return (
                <div key={episode.id}>
                  <TreeRowButton
                    itemKey={episodeKey}
                    indent={INDENT.episode}
                    active={episodeActive}
                    focused={rowFocused(episodeKey)}
                    bright
                    expanded={!episodeCollapsed}
                    onClick={() => {
                      setCursorKey(episodeKey)
                      // Шеврона нет, раскрывашка — сам ряд: чужая папка по клику открывается,
                      // уже выбранная переключается. Клик «посмотреть, что внутри» её не закрывает.
                      if (episodeActive || episodeCollapsed) toggleCollapsed(episodeKey)
                      onSelect({ section: "shots", node: { level: "episode", episodeId: episode.id } })
                    }}
                  >
                    <FolderTile open={!episodeCollapsed} accent />
                    <span className="truncate font-medium">
                      {episode.title ?? t("library.tree.episodeFallback", { number: episode.number })}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-ui-text-faint">
                      S{season.number}E{episode.number}
                    </span>
                    <NodeCount count={episodeTotal} />
                  </TreeRowButton>
                  {!episodeCollapsed ? (
                    <div className="relative">
                      <TreeGuide left={INDENT.episode} />
                      {/* Аниматик эпизода — над сценами: это материал всей серии, а не одной сцены. */}
                      {episode.animatic_segment_count > 0 ? (
                        <TreeRowButton
                          itemKey={`an${episode.id}`}
                          indent={INDENT.scene}
                          active={shotsNode?.level === "animatic" && shotsNode.episodeId === episode.id}
                          focused={rowFocused(`an${episode.id}`)}
                          onClick={() => {
                            setCursorKey(`an${episode.id}`)
                            onSelect({ section: "shots", node: { level: "animatic", episodeId: episode.id } })
                          }}
                        >
                          <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                            <Film className="h-3 w-3 text-ui-text-faint" />
                          </TreeTile>
                          <span className="truncate">{t("library.tree.animatic")}</span>
                          <NodeCount count={episode.animatic_segment_count} />
                        </TreeRowButton>
                      ) : null}
                      {episode.scenes.map((scene) => {
                        const sceneKey = collapseKey("scene", scene.id)
                        const sceneCollapsed = collapsed.has(sceneKey)
                        const sceneActive = shotsNode?.level === "scene" && shotsNode.sceneId === scene.id
                        return (
                          <div key={scene.id}>
                            <TreeRowButton
                              itemKey={sceneKey}
                              indent={INDENT.scene}
                              active={sceneActive}
                              focused={rowFocused(sceneKey)}
                              expanded={!sceneCollapsed}
                              onClick={() => {
                                setCursorKey(sceneKey)
                                if (sceneActive || sceneCollapsed) toggleCollapsed(sceneKey)
                                onSelect({ section: "shots", node: { level: "scene", sceneId: scene.id } })
                              }}
                            >
                              <FolderTile open={!sceneCollapsed} />
                              <span className="truncate">
                                {t("library.tree.sceneLabel", { number: scene.number })}
                                {scene.title ? ` · ${scene.title}` : ""}
                              </span>
                              <NodeCount count={scene.shots.length} />
                            </TreeRowButton>
                            {!sceneCollapsed ? (
                              <div className="relative">
                                <TreeGuide left={INDENT.scene} />
                                {scene.shots.map((shot) => {
                                  const shotKey = `sh${shot.id}`
                                  const shotActive = shotsNode?.level === "shot" && shotsNode.shotId === shot.id
                                  return (
                                    <TreeRowButton
                                      key={shot.id}
                                      itemKey={shotKey}
                                      indent={INDENT.shot}
                                      active={shotActive}
                                      focused={rowFocused(shotKey)}
                                      onClick={() => {
                                        setCursorKey(shotKey)
                                        onSelect({ section: "shots", node: { level: "shot", shotId: shot.id } })
                                      }}
                                    >
                                      <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                                        {shot.poster_url ? (
                                          <img
                                            src={shot.poster_url}
                                            alt=""
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <Film className="h-3 w-3 text-ui-text-faint" />
                                        )}
                                      </TreeTile>
                                      <span className="truncate">
                                        {t("library.tree.shotLabel", { number: `${scene.number}.${shot.number}` })}
                                      </span>
                                      {shot.shot_kind === "transition" ? (
                                        // Транзишен — событие, а не свойство: метка жёлтая, не серая.
                                        <ArrowRightLeft
                                          className="h-3 w-3 shrink-0 text-ui-status-rendering"
                                          aria-label={t("library.tree.transitionShot")}
                                        />
                                      ) : null}
                                      <NodeCount count={shot.asset_count} tone="muted" />
                                    </TreeRowButton>
                                  )
                                })}
                              </div>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ))
      )}

      {/* Загрузки стоят сразу за Шотами: это рабочая папка проекта, куда кладут исходники,
          и открывают её чаще, чем медиа отдельного героя или доску Холста. */}
      <FlatFolderRow
        itemKey="uploads"
        label={t("library.sections.uploads")}
        icon={SECTION_ICON.uploads}
        count={uploads.count}
        active={selection.section === "uploads"}
        focused={rowFocused("uploads")}
        onClick={() => {
          setCursorKey("uploads")
          onSelect({ section: "uploads" })
        }}
      />

      {/* Медиа сущностей: три одинаковые секции, различаются только видом записи.
          Пустую секцию не рисуем вовсе — заголовок с «пока нет …» это шум; секция
          появится сама, как только в проекте заведут первого героя, локацию или реквизит. */}
      {LIBRARY_ENTITY_SECTIONS.map((section) => {
        const rows = entities[section]
        if (rows.length === 0) return null
        return (
          <div key={section}>
            <SectionHeader label={t(`library.sections.${section}`)} icon={SECTION_ICON[section]} className="pt-3.5" />
            {rows.map((row) => {
              const rowKey = `${section}:${row.id}`
              const active = selection.section === section && selection.entityId === row.id
              return (
                <TreeRowButton
                  key={row.id}
                  itemKey={rowKey}
                  indent={INDENT.episode}
                  active={active}
                  focused={rowFocused(rowKey)}
                  onClick={() => {
                    setCursorKey(rowKey)
                    onSelect({ section, entityId: row.id })
                  }}
                >
                  <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                    {row.poster_url ? (
                      <img src={row.poster_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon className="h-3 w-3 text-ui-text-faint" />
                    )}
                  </TreeTile>
                  <span className="truncate">{row.name}</span>
                  <NodeCount count={row.asset_count} />
                </TreeRowButton>
              )
            })}
          </div>
        )
      })}

      <SectionHeader label={t("library.sections.canvas")} icon={SECTION_ICON.canvas} className="pt-3.5" />
      {canvases.length === 0 ? (
        <p className="px-2 text-xs text-ui-text-muted">{t("library.tree.noCanvases")}</p>
      ) : (
        canvases.map((board) => {
          const boardKey = `c${board.id}`
          const active = selection.section === "canvas" && selection.canvasId === board.id
          return (
            <TreeRowButton
              key={board.id}
              itemKey={boardKey}
              indent={INDENT.episode}
              active={active}
              focused={rowFocused(boardKey)}
              onClick={() => {
                setCursorKey(boardKey)
                onSelect({ section: "canvas", canvasId: board.id })
              }}
            >
              <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                <ImageIcon className="h-3.5 w-3.5 text-ui-text-muted" />
              </TreeTile>
              <span className="truncate">
                <span className="mr-1.5 text-ui-text-muted tabular-nums">#{board.number}</span>
                {board.title}
              </span>
              <NodeCount count={board.generation_count} />
            </TreeRowButton>
          )
        })
      )}

      <SectionHeader label={t("library.sections.flow")} icon={SECTION_ICON.flow} className="pt-3.5" />
      {flows.length === 0 ? (
        <p className="px-2 text-xs text-ui-text-muted">{t("library.tree.noFlows")}</p>
      ) : (
        flows.map((board) => {
          if (isLibraryFlowOrphan(board)) {
            const boardKey = "flow-orphans"
            const active = selection.section === "flow" && selection.orphans === true
            return (
              <TreeRowButton
                key={boardKey}
                itemKey={boardKey}
                indent={INDENT.episode}
                active={active}
                focused={rowFocused(boardKey)}
                onClick={() => {
                  setCursorKey(boardKey)
                  onSelect({ section: "flow", flowId: null, orphans: true })
                }}
              >
                <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                  <Workflow className="h-3.5 w-3.5 text-ui-text-muted" />
                </TreeTile>
                <span className="truncate">{board.title}</span>
                <NodeCount count={board.asset_count} />
              </TreeRowButton>
            )
          }
          const boardKey = `f${board.id}`
          const active = selection.section === "flow" && selection.flowId === board.id
          return (
            <TreeRowButton
              key={board.id}
              itemKey={boardKey}
              indent={INDENT.episode}
              active={active}
              focused={rowFocused(boardKey)}
              onClick={() => {
                setCursorKey(boardKey)
                onSelect({ section: "flow", flowId: board.id })
              }}
            >
              <TreeTile className="border-ui-border-strong bg-ui-surface-raised">
                <Workflow className="h-3.5 w-3.5 text-ui-text-muted" />
              </TreeTile>
              <span className="truncate">
                <span className="mr-1.5 text-ui-text-muted tabular-nums">#{board.number}</span>
                {board.title}
              </span>
              <NodeCount count={board.asset_count} />
            </TreeRowButton>
          )
        })
      )}

      {/* Эскизы — в самом низу: это черновой материал редактора эскизов, к нему
          возвращаются реже всего. */}
      <FlatFolderRow
        itemKey="sketches"
        label={t("library.sections.sketches")}
        icon={SECTION_ICON.sketches}
        count={sketches.count}
        active={selection.section === "sketches"}
        focused={rowFocused("sketches")}
        onClick={() => {
          setCursorKey("sketches")
          onSelect({ section: "sketches" })
        }}
      />
    </div>
  )
}
